import {
  LinkField,
  RichTextField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse as CongestionReportDetails } from '@alliander-fe/api';
import { ComponentProps } from 'src/jss/lib/component-props';
import { CongestionReportsParagraph as CongestionReportsParagraphComponent } from 'src/components/CongestionReportsParagraph';
import {
  FilterValuePropertyFromFields,
  mapWithoutValueProperty,
} from 'src/utils/mapWithoutValueProperty';
import { useSitecoreContext } from '@liander/context';

type CongestionReportsParagraphProps = ComponentProps & {
  fields: {
    attachmentText: RichTextField;
    attachmentTitle: TextField;

    availableCapacityHigherThanRequiredCapacityText?: RichTextField;

    congestionManagementTitle: TextField;
    congestionManagementApplicable: TextField;
    congestionManagementNotApplicable: TextField;
    congestionManagementNotYetApplicable: TextField;

    downloadReportButtonLabel: TextField;

    expressInterestButton: LinkField;
    expressInterestText: RichTextField;

    consumeResearchCongestionManagementText: RichTextField;
    supplyResearchCongestionManagementText: RichTextField;

    causeCapacityText: TextField;
    causeVoltageText: TextField;
    causeGridSecurityText: TextField;

    highAndMediumVoltageIntroText: RichTextField;
    highAndMediumVoltageCapacityTextFirstPart: RichTextField;
    highAndMediumVoltageCapacityTextSecondPart: RichTextField;
    highAndMediumVoltageHowAndWhenText: RichTextField;
    highAndMediumVoltageNotableAspectsText: RichTextField;

    highVoltageCapacityTextFirstPart: RichTextField;
    highVoltageCapacityTextSecondPart: RichTextField;
    highVoltageHowAndWhenText: RichTextField;
    highVoltageIntroText: RichTextField;
    highVoltageNotableAspectsText: RichTextField;

    mediumVoltageCapacityTextFirstPart: RichTextField;
    mediumVoltageCapacityTextSecondPart: RichTextField;
    mediumVoltageHowAndWhenText: RichTextField;
    mediumVoltageIntroText: RichTextField;
    mediumVoltageNotableAspectsText: RichTextField;

    enableResolvedDateRange: { value: boolean };
  };
};

export type EnrichedCongestionReportHeroProps = {
  fields: {
    isLoading?: { value: boolean };
    isError?: { value: boolean };
    congestionReport?: { value: CongestionReportDetails };
  };
};

type CombinedCongestionReportHeroProps = CongestionReportsParagraphProps &
  EnrichedCongestionReportHeroProps;

export type FilteredCongestionReportsParagraphProps = FilterValuePropertyFromFields<
  CombinedCongestionReportHeroProps['fields']
>;

const CongestionReportsParagraph = (props: CongestionReportsParagraphProps): JSX.Element => {
  const { isEditor } = useSitecoreContext();

  if (!props.fields) return <></>;

  // Add mock data to the props if the component is in editor mode.
  const propsWithMockData = isEditor
    ? {
        ...props,
        fields: {
          ...props.fields,
          isLoading: { value: false },
          isError: { value: false },
          congestionReport: { value: congestionReportDetailsMockData },
        },
      }
    : props;

  const fields = mapWithoutValueProperty(propsWithMockData.fields);

  return <CongestionReportsParagraphComponent {...fields} />;
};

// We added the mock data so we can see the component correctly in the editor.
const congestionReportDetailsMockData: CongestionReportDetails = {
  id: '28B7CD39-4BFB-4B0B-A5C8-8DEA3A69E1F5',
  name: 'HILVERSUM 10-2i',
  assetType: 'Middenspanning',
  locations: 'Hilversum',
  congestionType: 'Verbruik',
  status: 'Lopend',
  solution: 'Capaciteitsuitbreiding',
  affectedPostalCodes: '1217AH',
  preAnnouncement: ['De bijzonderheden tekst onder het kopje bijzonderheden'],
  congestionManagementResearchSummary:
    'De bijzonderheden tekst van de samenvatting van het congestiemanagementonderzoek',
  congestionManagementApplicable: 'NOTYET',
  availableTransportCapacity: 1,
  requiredTransportCapacityInSolutionYear: 3,
  contentServerLink: 'https://liander.tst.dxp.alliander.com/-/media/Files/CM-rapportages/Test',
  announcementDate: '2020-10-21T00:00:00',
  completionDate: '2021-10-21T00:00:00',
  resolvedDate: '2024-12-31T00:00:00',
  updatedAtDate: '2020-10-28T00:00:00',
  congestionManagementPublicationDate: '2021-10-28T00:00:00',
  underlyingAssets: [
    {
      name: 'Routenaam',
      expectedEndDate: '2027-12-11T00:00:00',
      cause: 'Geen capaciteit',
    },
    {
      name: 'Routenaam',
      expectedEndDate: '2027-07-27T00:00:00',
      cause: 'Defecte leiding',
    },
    {
      name: 'Stationsnaam',
      expectedEndDate: '2026-04-19T00:00:00',
      cause: 'Geen capaciteit',
    },
  ],
  scarcityLevel: 'MainAssetOnly',
};

export default withDatasourceCheck()<CongestionReportsParagraphProps>(CongestionReportsParagraph);
